import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import {
  Phone, MessageCircle, Calendar, MapPin, Stethoscope, ShieldCheck, Clock,
  Award, HeartHandshake, Sparkles, Video, Wallet, Scale, Activity, UserCheck,
  Star, ChevronRight
} from "lucide-react";
import { CLINIC, waLink, telLink } from "@/lib/clinic";
import { SERVICES } from "@/data/services";
import SectionLabel from "@/components/SectionLabel";
import { api } from "@/lib/api";

const WHY = [
  { icon: Clock,       t: "35+ Years of Experience" },
  { icon: Award,       t: "Former AGM (Medical), BHEL" },
  { icon: HeartHandshake, t: "Trusted Family Physician" },
  { icon: UserCheck,   t: "Personalized Healthcare" },
  { icon: Wallet,      t: "Affordable Consultation" },
  { icon: Scale,       t: "Ethical Medical Practice" },
  { icon: Activity,    t: "Modern Diagnostic Support" },
  { icon: Sparkles,    t: "Clean & Hygienic Clinic" },
  { icon: Calendar,    t: "Easy Online Appointment" },
  { icon: Video,       t: "Teleconsultation Available" },
];

function Stat({ n, label }) {
  return (
    <div className="stat-card rounded-2xl px-5 py-4 aarogya-shadow" data-testid={`stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="font-serif text-3xl md:text-4xl text-slate-900">{n}</div>
      <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mt-1">{label}</div>
    </div>
  );
}

export default function Home() {
  const [testis, setTestis] = useState([]);
  useEffect(() => { api.get("/testimonials").then(r => setTestis(r.data)).catch(()=>{}); }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative hero-gradient overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-20 md:pt-20 md:pb-28 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-white/60 backdrop-blur px-3 py-1 text-[11px] tracking-[0.18em] uppercase text-teal-700 font-semibold">
              <Stethoscope className="h-3.5 w-3.5" /> Bhopal · Ayodhya Bypass Road
            </div>
            <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05]">
              35+ Years of <span className="italic text-[#0A6EBD]">Trusted Healthcare</span><br />
              with <span className="text-[#16A5B8]">Compassion</span> & Excellence.
            </h1>
            <p className="mt-6 max-w-xl text-slate-600 text-base sm:text-lg leading-relaxed">
              Ethical, affordable and evidence-based medicine for families across Bhopal — from
              chronic disease management to same-day consultations, delivered with warmth by
              <strong className="text-slate-800"> Dr. V. K. Onkar</strong>.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/appointment" className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white shadow-md hover:shadow-lg transition"
                    style={{ background: "#0A6EBD" }} data-testid="hero-book-btn">
                <Calendar className="h-4 w-4" /> Book Appointment
              </Link>
              <a href={telLink()} className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white shadow-md hover:shadow-lg transition"
                 style={{ background: "#E11D48" }} data-testid="hero-call-btn">
                <Phone className="h-4 w-4" /> Call Now
              </a>
              <a href={waLink("Hello Aarogya Clinic, I need help.")} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white shadow-md hover:shadow-lg transition"
                 style={{ background: "#25D366" }} data-testid="hero-whatsapp-btn">
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(CLINIC.mapsQuery)}`}
                 target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-slate-800 bg-white border border-slate-200 hover:bg-slate-50 transition"
                 data-testid="hero-directions-btn">
                <MapPin className="h-4 w-4" /> Directions
              </a>
            </div>

            <div className="mt-8 flex items-center gap-4 text-sm">
              <div className="inline-flex items-center gap-2 rounded-full bg-rose-50 border border-rose-200 px-3 py-1.5 text-rose-700 font-semibold">
                <Phone className="h-3.5 w-3.5" /> Emergency: {CLINIC.phoneDisplay}
              </div>
              <div className="text-slate-500 hidden sm:block">Mon–Sat · 5:00 PM – 8:00 PM · Sun by appointment</div>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl">
              <Stat n="35+" label="Years" />
              <Stat n="10K+" label="Patients" />
              <Stat n="4.9★" label="Rating" />
              <Stat n="24×7" label="Emergency" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="absolute -inset-6 rounded-[2.5rem] blur-2xl opacity-30"
                   style={{ background: "radial-gradient(closest-side,#16A5B8,transparent)" }} />
              <div className="relative rounded-[2rem] overflow-hidden aarogya-shadow-lg border border-white bg-white">
                <img src={CLINIC.doctor.photo} alt="Dr. V. K. Onkar at Aarogya Clinic"
                     className="w-full h-[520px] object-cover" data-testid="hero-doctor-photo" />
                <div className="absolute left-4 right-4 bottom-4 rounded-2xl bg-white/85 backdrop-blur border border-white/70 px-4 py-3">
                  <div className="font-serif text-lg text-slate-900">{CLINIC.doctor.name}</div>
                  <div className="text-xs text-slate-600">{CLINIC.doctor.quals} · {CLINIC.doctor.experience}</div>
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 stat-card rounded-2xl px-4 py-3 hidden md:block">
                <div className="text-[10px] uppercase tracking-[0.22em] text-teal-600 font-semibold">Ethical Practice</div>
                <div className="font-serif text-base text-slate-900">No unnecessary tests · No over-prescription</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WELCOME */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <SectionLabel>A note from the doctor</SectionLabel>
          <p className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-[1.35] text-slate-800 tracking-tight">
            “Medicine, for me, has always been about <em className="text-[#0A6EBD]">people</em> — not prescriptions.
            Come as you are; we listen carefully, treat honestly, and stay with your family for the long run.”
          </p>
          <div className="mt-6 text-sm text-slate-500 uppercase tracking-[0.24em]">— Dr. V. K. Onkar</div>
        </div>
      </section>

      {/* WHY */}
      <section className="py-16 lg:py-24" style={{ background: "#F1F6FB" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-10">
            <div>
              <SectionLabel>Why Choose Us</SectionLabel>
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-slate-900">
                Family medicine, done with old-school care and modern tools.
              </h2>
            </div>
            <Link to="/about" className="text-sm font-semibold text-[#0A6EBD] hover:underline inline-flex items-center gap-1">
              Learn about us <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid gap-3 sm:gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
            {WHY.map((w, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200/60 p-5 aarogya-shadow hover:-translate-y-0.5 transition"
                   data-testid={`why-item-${i}`}>
                <w.icon className="h-6 w-6 text-[#0A6EBD]" />
                <div className="mt-3 font-semibold text-slate-800 text-sm leading-snug">{w.t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <SectionLabel>Our Services</SectionLabel>
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-slate-900 max-w-2xl">
                Full-spectrum care under one warm, well-run roof.
              </h2>
            </div>
            <Link to="/services" className="text-sm font-semibold text-[#0A6EBD] hover:underline">View all services →</Link>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.slice(0, 6).map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`}
                    className="group rounded-2xl border border-slate-200/70 bg-white p-6 aarogya-shadow hover:-translate-y-1 hover:shadow-lg transition"
                    data-testid={`service-card-${s.slug}`}>
                <div className="h-11 w-11 rounded-xl grid place-items-center text-white"
                     style={{ background: "linear-gradient(135deg,#0A6EBD,#16A5B8)" }}>
                  <s.icon className="h-5 w-5" />
                </div>
                <div className="mt-4 font-serif text-xl text-slate-900 group-hover:text-[#0A6EBD]">{s.title}</div>
                <div className="mt-2 text-sm text-slate-600 leading-relaxed">{s.short}</div>
                <div className="mt-4 text-[13px] font-semibold text-[#0A6EBD] inline-flex items-center gap-1">
                  Learn more <ChevronRight className="h-4 w-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 lg:py-24" style={{ background: "linear-gradient(180deg,#0A1F3D,#0C2A54)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-white">
          <div className="text-[11px] font-bold tracking-[0.24em] uppercase text-teal-300 mb-3">Patient Stories</div>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-white max-w-2xl">
            Kindness remembered. Care that comes back.
          </h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {testis.slice(0, 6).map((t, i) => (
              <div key={t.id || i} className="rounded-2xl bg-white/5 border border-white/10 p-6 backdrop-blur"
                   data-testid={`testimonial-${i}`}>
                <div className="flex items-center gap-1 text-amber-300">
                  {Array.from({ length: t.rating || 5 }).map((_, k) => <Star key={k} className="h-4 w-4 fill-current" />)}
                </div>
                <p className="mt-3 text-slate-100 leading-relaxed italic">“{t.quote}”</p>
                <div className="mt-4 text-sm">
                  <div className="font-semibold text-white">{t.patient_name}</div>
                  {t.location && <div className="text-slate-400 text-xs">{t.location}</div>}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/testimonials" className="inline-flex items-center gap-1 text-teal-300 font-semibold hover:underline">
              Read all stories <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* QUICK ACTIONS */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionLabel>Quick Actions</SectionLabel>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-slate-900 max-w-2xl">One tap. Real help.</h2>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Link to="/appointment" className="rounded-2xl p-6 text-white aarogya-shadow hover:-translate-y-0.5 transition"
                  style={{ background: "#0A6EBD" }} data-testid="quick-book">
              <Calendar className="h-6 w-6" />
              <div className="mt-3 font-serif text-xl">Book Appointment</div>
              <div className="text-sm opacity-90 mt-1">Pick a slot online in seconds</div>
            </Link>
            <Link to="/teleconsultation" className="rounded-2xl p-6 text-white aarogya-shadow hover:-translate-y-0.5 transition"
                  style={{ background: "#3F2D86" }} data-testid="quick-teleconsult">
              <Video className="h-6 w-6" />
              <div className="mt-3 font-serif text-xl">Video Consultation</div>
              <div className="text-sm opacity-90 mt-1">See the doctor from home</div>
            </Link>
            <a href={waLink("Hello Aarogya Clinic")} target="_blank" rel="noreferrer"
               className="rounded-2xl p-6 text-white aarogya-shadow hover:-translate-y-0.5 transition"
               style={{ background: "#25D366" }} data-testid="quick-whatsapp">
              <MessageCircle className="h-6 w-6" />
              <div className="mt-3 font-serif text-xl">WhatsApp Chat</div>
              <div className="text-sm opacity-90 mt-1">Quick queries & reports</div>
            </a>
            <a href={telLink()} className="rounded-2xl p-6 text-white aarogya-shadow hover:-translate-y-0.5 transition"
               style={{ background: "#E11D48" }} data-testid="quick-call">
              <Phone className="h-6 w-6" />
              <div className="mt-3 font-serif text-xl">Call Clinic</div>
              <div className="text-sm opacity-90 mt-1">Talk to our front desk</div>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
